/**
 * FieldChatDrawer — field-side chat surface as a bottom-sheet over the
 * LiveKit grid. Two states:
 *
 *   - **Collapsed (default)**: small floating "Chat" button bottom-right,
 *     with an unread badge when new messages arrived since the last open.
 *     Pointer-events confined to the button itself so the operator's
 *     screen-share / camera grid stays fully tappable underneath.
 *
 *   - **Expanded**: full-width sheet sliding up from the bottom (60% of
 *     viewport height by default), hosting the shared <ChatPanel /> with
 *     the field path enabled (X-Field-Session-Token + bus from the field's
 *     CommandBus, lifted into useSessionBus by <InRoomAnnotation />).
 *
 * Unread tracking lives in this component rather than inside ChatPanel so
 * the badge keeps counting while collapsed (ChatPanel only mounts when
 * the sheet is open). Both surfaces share react-query's
 * `["session-chat", sessionId]` cache, so the badge's count and ChatPanel's
 * rendered thread always agree.
 *
 * Mounted from FieldRoom INSIDE <InRoomAnnotation> so the bus is
 * populated. The backend chat views are dual-auth — the field's
 * fieldSessionToken authorises send + list.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CHAT_MESSAGE_TYPE,
  CHAT_TOPIC,
  listChat,
} from "@/lib/chat";
import type { Envelope } from "@/lib/commandBus";
import { useSessionBus } from "@/lib/sessionBus";
import { playSound } from "@/lib/soundEffects";
import type { ChatMessage, ChatPage } from "@/lib/types";

import { ChatPanel } from "./ChatPanel";


type Props = {
  sessionId: string;
  fieldSessionToken: string;
};


export function FieldChatDrawer({ sessionId, fieldSessionToken }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const bus = useSessionBus((s) => s.bus);
  const [open, setOpen] = useState(false);
  // Anchor for "what's been seen" — anything created after this counts as
  // unread. Initialised to now() so the badge starts at zero (the field
  // user already had a chance to read whatever existed when they joined
  // the room — those messages will be in the thread on first open).
  const lastSeenAtRef = useRef<string>(new Date().toISOString());
  const [unread, setUnread] = useState(0);

  // Always-on REST query so the badge has fresh data even while collapsed.
  // Shares the cache key + dedups with ChatPanel's own useQuery — when the
  // sheet opens, ChatPanel reads the same cache without re-fetching.
  const q = useQuery({
    queryKey: ["session-chat", sessionId],
    queryFn: async () => listChat(sessionId, { fieldSessionToken }),
    refetchInterval: 8000,
  });

  // Always-on bus listener so the badge bumps the moment a peer message
  // lands, not on the next 8s poll. CommandBus.on is additive (per the
  // 1B-C2 refactor) so ChatPanel's identical listener inside the sheet
  // coexists without conflict — both upserts hit the same query cache
  // and dedup by id.
  useEffect(() => {
    if (!bus) return;
    const unsub = bus.on<{ message: ChatMessage }>(
      `${CHAT_TOPIC}/${CHAT_MESSAGE_TYPE}`,
      (env: Envelope<{ message: ChatMessage }>) => {
        const incoming = env.payload?.message;
        if (!incoming?.id) return;
        qc.setQueryData<ChatPage | undefined>(
          ["session-chat", sessionId],
          (prev) => {
            if (!prev) return { results: [incoming], has_more: false };
            if (prev.results.some((m) => m.id === incoming.id)) return prev;
            return { ...prev, results: [...prev.results, incoming] };
          },
        );
        // Notification chime — only for peer messages and only when the
        // drawer is closed (open + bus-listener self-author check would
        // double-fire). author_participant === null marks an operator
        // message from the field's POV.
        if (incoming.author_participant === null) {
          void playSound("notify");
        }
      },
    );
    return () => {
      unsub();
    };
  }, [bus, qc, sessionId]);

  // Recompute the unread count whenever the cache changes OR the drawer
  // opens (advance the lastSeen anchor on open). Authorship filter: a
  // message authored by THIS field user shouldn't count as unread for them
  // — that would always be 1 right after they send.
  useEffect(() => {
    const messages = q.data?.results ?? [];
    if (open) {
      // Mark everything currently visible as seen.
      const last = messages[messages.length - 1];
      if (last) lastSeenAtRef.current = last.created_at;
      setUnread(0);
      return;
    }
    const anchor = lastSeenAtRef.current;
    let count = 0;
    for (const m of messages) {
      if (m.created_at <= anchor) continue;
      // Skip own messages — only peer / system rows are "unread" from
      // the field's perspective.
      if (m.author_participant !== null) continue;
      count++;
    }
    setUnread(count);
  }, [q.data?.results, open]);

  const ariaUnread = useMemo(
    () =>
      unread > 0 ? t("ui.fieldChatDrawer.unreadSuffix", { count: unread }) : "",
    [unread, t],
  );

  return (
    <>
      {/* Collapsed: floating launcher. pointer-events-none on the wrapper
          keeps everything else tappable; the button itself opts back in
          via pointer-events-auto. */}
      {!open && (
        <div className="pointer-events-none fixed inset-x-0 bottom-3 z-40 flex justify-end px-3">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="pointer-events-auto relative flex items-center gap-2 rounded-full border border-nb-border bg-nb-bg/90 px-4 py-2 text-sm font-medium text-nb-text shadow-lg backdrop-blur hover:bg-nb-surface"
            aria-label={`${t("ui.fieldChatDrawer.openChat")}${ariaUnread}`}
          >
            <span aria-hidden>💬</span>
            <span>{t("ui.fieldChatDrawer.chat")}</span>
            {unread > 0 && (
              <span
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-cvp-danger px-1 text-[10px] font-bold text-white"
                aria-hidden
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Expanded: dimmed backdrop + bottom sheet. The sheet stops pointer
          propagation so taps inside the chat don't dismiss it; the
          backdrop is the dismiss target. */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/40"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t("ui.fieldChatDrawer.sessionChat")}
        >
          {/* Spacer above the sheet — clicking it dismisses. */}
          <div className="flex-1" />
          <div
            // `dvh` units track the iOS Safari dynamic viewport (address
            // bar shrink/grow), so the sheet doesn't get scissored when
            // the keyboard pops up for the composer.
            className="flex flex-col rounded-t-2xl border-t border-nb-border bg-nb-bg/95 p-3 shadow-2xl"
            style={{ height: "60dvh", maxHeight: "60dvh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Grab handle — purely visual; tap-to-close is the
                    backdrop. */}
                <span
                  aria-hidden
                  className="block h-1 w-10 rounded-full bg-slate-600"
                />
                <h2 className="text-sm font-semibold text-nb-text">
                  {t("ui.fieldChatDrawer.chat")}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-ghost px-2 py-1 text-xs"
                aria-label={t("ui.fieldChatDrawer.closeChat")}
              >
                {t("ui.fieldChatDrawer.close")}
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <ChatPanel
                sessionId={sessionId}
                fieldSessionToken={fieldSessionToken}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
